import { useCallback, useEffect, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { useLocalStorage } from "usehooks-ts";

import { QuestionIcon } from "../../../../components/icons/QuestionIcon";
import {
  type IOption,
  StyledSelect,
} from "../../../../components/StyledSelect/StyledSelect";
import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";
import { useUser } from "../../../../contexts/userContext";
import type { ResponseFormatType } from "../../types";
import { SchemaItem } from "./SchemaItem";
import { SchemaModal } from "./SchemaModal";

const options = [
  { value: "text", label: "text" },
  { value: "json_object", label: "json_object" },
  { value: "json_schema", label: "json_schema" },
];

export interface ResponseFormatProps {
  responseFormat: ResponseFormatType;
  setResponseFormat: (responseFormat: ResponseFormatType) => void;
}

const ResponseFormat: React.FC<ResponseFormatProps> = ({
  responseFormat,
  setResponseFormat,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { user } = useUser();

  const [formatsFromLS, setLSFormat] = useLocalStorage<Record<string, Object>>(
    "respFormats",
    {},
  );

  const setFormat = (format: IOption) => {
    setResponseFormat({ ...responseFormat, type: format.value });
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCancel = () => {
    setFormat({ value: "text", label: "text" });
    setShowModal(false);
  };

  useEffect(() => {
    if (responseFormat.type === "json_schema" && !responseFormat.json_schema) {
      handleOpenModal();
    }
  }, [responseFormat]);

  const saveSchema = (schema: ResponseFormatType) => {
    if (!user?.sub) return;
    setLSFormat(prev => {
      if (!schema?.json_schema?.name) return prev;

      return {
        ...prev,
        [user.sub]: {
          ...prev[user.sub],
          [schema.json_schema.name]: schema.json_schema,
        },
      };
    });

    setResponseFormat(schema);
    setShowModal(false);
  };

  const removeUserFormat = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, name: string) => {
      e.stopPropagation();
      if (!user?.sub) return;

      setLSFormat(prev => {
        return {
          ...prev,
          [user.sub]: {
            ...prev[user.sub],
            [name]: undefined,
          },
        };
      });
    },
    [],
  );

  return (
    <>
      <div className="playground-controls-item">
        <label>
          Response format
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip(
              "Messages must contain the word 'json' in some form, to use 'response_format' of type 'json_object'.",
            )}
          >
            <span className="title-question-icon">
              <QuestionIcon />
            </span>
          </OverlayTrigger>
        </label>
        <StyledSelect
          options={options}
          onChange={setFormat}
          value={options.find(option => option.value === responseFormat.type)}
        />
        {responseFormat.type === "json_schema" &&
          responseFormat.json_schema && (
            <SchemaItem
              name={responseFormat.json_schema.name as string}
              handleEdit={handleOpenModal}
            />
          )}
      </div>
      {showModal && (
        <SchemaModal
          schema={responseFormat?.json_schema}
          handleClose={handleCloseModal}
          handleSaveSchema={saveSchema}
          onCancel={handleCancel}
          usersFormats={
            user?.sub && formatsFromLS[user?.sub]
              ? formatsFromLS[user?.sub]
              : {}
          }
          removeUserFormat={removeUserFormat}
        />
      )}
    </>
  );
};

export { ResponseFormat };
